<template>
  <modal ref="modal" :show-on-mount="true" :click-outside-to-close="false" headerless size="md"
         modal-id="verify-mfa-token-modal" @opened="opened">
    <template #body>
      <form>
        <h2 id="mfa-token-heading" class="mt-3 title">
          {{ i18n.t('devise.submit_token_title') }}
        </h2>

        <p v-if="errorMessage" class="text-danger mb-4" v-html="errorMessage" />

        <template v-if="!hideInputs">
          <div class="d-grid gap-3">
            <div>
              <input id="mfa-token" ref="tokenField" v-model="token" name="token"
                     autocomplete="one-time-code" inputmode="numeric" pattern="[0-9]*"
                     aria-labelledby="mfa-token-heading" class="form-control form-control-lg">

              <small class="form-text text-muted">
                {{ tokenHelpText }}
              </small>
            </div>

            <checkbox-input checkbox-id="mfa-remember-device-checkbox" input-name="remember_device"
                            :label="i18n.t('devise.remember_device')" @change="updateRememberDevice" />

            <submit-button :label="i18n.t('devise.submit_token')" :submit-in-progress="submitting"
                           class="btn-lg modal-button" @click="submit" />

            <div v-if="supportsSms" class="d-flex justify-content-center mt-2 mb-4">
              <link-button @click="requestSms">
                {{ i18n.t('devise.request_sms') }}
              </link-button>
              <success-check ref="requestSmsSuccessCheck" />
            </div>

            <div v-if="supportsRecoveryCodes" class="d-flex justify-content-center mt-2 mb-4">
              <a :href="useRecoveryCodePath">{{ i18n.t('member.account.verify_mfa_token.use_recovery_code') }}</a>
            </div>
          </div>
        </template>
      </form>
    </template>
  </modal>
</template>

<script>
import ajax from '../helpers/ajax'
import CheckboxInput from '../components/inputs/checkbox-input'
import LinkButton from '../components/buttons/link-button'
import Modal from '../components/modal'
import SubmitButton from '@/components/buttons/submit-button'
import SuccessCheck from '../components/check-success-inline'

const REQUEST_SMS_PATH = '/users/request-sms'

export default {
  components: { CheckboxInput, LinkButton, Modal, SubmitButton, SuccessCheck },

  props: {
    // A URL we can poll to see if the user has verified via an alternative means.
    // Only supported by some MFA strategies.
    statusCheckPath: {
      type: String,
      required: false,
      default: null
    },
    supportsRecoveryCodes: {
      type: Boolean,
      default: false
    },
    supportsSms: {
      type: Boolean,
      default: false
    },
    tokenHelpText: {
      type: String,
      required: true
    },
    userId: {
      type: Number,
      required: true
    },
    useRecoveryCodePath: {
      type: String,
      required: false
    },
    verifyTokenPath: {
      type: String,
      required: true
    }
  },

  emits: ['authenticated', 'authenticity-token-updated', 'missing-info-required'],

  data() {
    return {
      errorMessage: null,
      hideInputs: false,
      statusCheckPollingIntervalId: null,
      submitting: false,
      rememberDevice: false,
      token: null
    }
  },

  mounted() {
    if (this.statusCheckPath) {
      this.statusCheckPollingIntervalId = setInterval(() => { this.pollStatus() }, 3000)
    }
  },

  methods: {
    handleSuccess(response) {
      this.$refs.modal.close()

      if (response.authenticityToken) {
        this.$emit('authenticity-token-updated', response.authenticityToken)
      }

      if (response.requiresMissingInfo) {
        this.$emit('missing-info-required', {
          requiredFields: response.requiredFields,
          additionalFieldConfigs: response.additionalFieldConfigs,
          euDataProcessingConsentData: response.euDataProcessingConsentData,
          phoneNumberLabel: response.phoneNumberLabel,
          postcodeLabel: response.postcodeLabel
        })
      } else {
        this.$emit('authenticated', {
          afterSignInRedirect: response.afterSignInRedirect,
          userMenu: response.userMenu
        })
      }
    },

    opened() {
      this.$refs.tokenField.focus()
    },

    pollStatus() {
      fetch(this.statusCheckPath, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      }).then((response) => {
        if (response.status !== 202) {
          // 202 means keep polling, so if the status is anything else, stop polling.
          clearInterval(this.statusCheckPollingIntervalId)
        }

        if (response.status === 200) {
          // Success! Handle the response the same way we would success from a button click.
          response.json().then((response) => {
            this.handleSuccess(response)
          })
        }
      })
    },

    requestSms() {
      ajax.post({
        url: REQUEST_SMS_PATH,
        successHandler: () => {
          this.$refs.requestSmsSuccessCheck.toggle()
        }
      })
    },

    submit() {
      this.submitting = true

      ajax.post({
        url: this.verifyTokenPath,
        data: {
          token: this.token,
          remember_device: this.rememberDevice,
          user_id: this.userId
        },
        successHandler: (response) => {
          this.handleSuccess(response)
        },
        errorHandler:({ message, info }) => { 
          this.errorMessage = message
          this.token = null

          if (info.accountLocked) {
            this.hideInputs = true
          }
        }
      }).then(() => {
        this.submitting = false
      })
    },

    updateRememberDevice(value) {
      this.rememberDevice = value
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~common_styles/breakpoints" as breakpoints;

input {
  height: 58px;

  @include breakpoints.screen-mobile-only {
    height: 50px;
  }
}

.modal-button {
  text-transform: none;
  font-size: 1rem;
  padding: 1rem;
}
</style>
